import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, LoginCredentials, AuthResponse } from '../types';
import { authService } from '../services/authService';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (credentials: LoginCredentials) => Promise<{ success: boolean; organizations?: any[]; error?: string }>;
  logout: () => void;
  forceLogout: () => void; // New function for forced logout
  loading: boolean;
  organizations: any[] | null;
  selectOrganization: (orgID: number) => Promise<{ success: boolean; error?: string }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [organizations, setOrganizations] = useState<any[] | null>(null);
  const [tempCredentials, setTempCredentials] = useState<LoginCredentials | null>(null);

  // Global logout function that can be called from anywhere
  const forceLogout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('user');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('tempAccessToken');
    localStorage.removeItem('tempRefreshToken');
    localStorage.removeItem('organizations');
    setUser(null);
    setOrganizations(null);
    setTempCredentials(null);
    
    // Redirect to login page
    window.location.href = '/login';
  };

  // Make forceLogout available globally
  useEffect(() => {
    (window as any).forceLogout = forceLogout;
  }, []);

  useEffect(() => {
    // Restore authentication state from localStorage
    const accessToken = localStorage.getItem('accessToken');
    const userData = localStorage.getItem('user');
    const tempAccessToken = localStorage.getItem('tempAccessToken');
    
    if (accessToken && userData) {
      // User is fully authenticated
      try {
        const user = JSON.parse(userData);
        setUser(user);
        
        // Validate token (optional - you can add token validation logic here)
        // For now, we'll assume the token is valid if it exists
      } catch (error) {
        // Invalid user data, clear it
        forceLogout();
      }
    } else if (tempAccessToken) {
      // User is logged in but needs to select organization
      // This state will be handled by the login flow
    }
    
    setLoading(false);
  }, []);

  const login = async (credentials: LoginCredentials): Promise<{ success: boolean; organizations?: any[]; error?: string }> => {
    try {
      setLoading(true);
      const response = await authService.login(credentials);
      
      if (!response) {
        // User was redirected due to error
        return { success: false, error: 'Anmeldung fehlgeschlagen' };
      }
      
      if (response.accessToken) {
        localStorage.setItem('accessToken', response.accessToken);
        
        if (response.organizations) {
          // User needs to select an organization
          setOrganizations(response.organizations);
          setTempCredentials(credentials); // Store credentials for organization selection
          localStorage.setItem('tempAccessToken', response.accessToken);
          localStorage.setItem('organizations', JSON.stringify(response.organizations)); // Store organizations for later use
          if (response.refreshToken) {
            localStorage.setItem('tempRefreshToken', response.refreshToken);
          }
          return { success: true, organizations: response.organizations }; // Login successful but needs org selection
        } else if (response.user) {
          // Direct login successful
          localStorage.setItem('user', JSON.stringify(response.user));
          setUser(response.user);
          setTempCredentials(null); // Clear temp credentials
          return { success: true, organizations: undefined }; // Direct login, no org selection needed
        }
      }
      return { success: false, organizations: undefined };
    } catch (error: any) {
      console.log('AuthContext login error:', error);
      setTempCredentials(null); // Clear temp credentials on error
      return { success: false, organizations: undefined, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const selectOrganization = async (orgID: number): Promise<{ success: boolean; error?: string }> => {
    try {
      setLoading(true);
      
      if (!tempCredentials) {
        throw new Error('Keine temporären Anmeldedaten verfügbar');
      }
      
      const response = await authService.selectOrganization(orgID, tempCredentials);
      
      if (!response) {
        // User was redirected due to error
        return { success: false, error: 'Organisationsauswahl fehlgeschlagen' };
      }
      
      if (response.accessToken && response.user) {
        localStorage.setItem('accessToken', response.accessToken);
        localStorage.setItem('user', JSON.stringify(response.user));
        if (response.refreshToken) {
          localStorage.setItem('refreshToken', response.refreshToken);
        }
        
        // Clean up temp tokens and credentials
        localStorage.removeItem('tempAccessToken');
        localStorage.removeItem('tempRefreshToken');
        setTempCredentials(null);
        
        setUser(response.user);
        setOrganizations(null);
        return { success: true };
      }
      return { success: false };
    } catch (error: any) {
      setTempCredentials(null); // Clear temp credentials on error
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('user');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('tempAccessToken');
    localStorage.removeItem('tempRefreshToken');
    localStorage.removeItem('organizations'); // Clear organizations data
    setUser(null);
    setOrganizations(null);
    setTempCredentials(null);
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user && !!localStorage.getItem('accessToken'),
    login,
    logout,
    forceLogout,
    loading,
    organizations,
    selectOrganization,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 